/**
 * @file PageVersionsDialog.tsx
 * @description «Все версии» страницы — на весь экран, как «История» вида
 * сайта (phase19.16 Р3а; uiux-r3 §3.1; SB126, SB63): номер, дата, автор,
 * заметка или сводка правок, «на сайте»; [Смотреть] — версия на канве,
 * [Вернуть] — окно с предупреждениями (право публикации и контента).
 * По 20 версий, «Показать ещё». Поиск по дате — [уточнить], в проекте
 * упомянут, сервер фильтра по дате не даёт (только `before`).
 */

import { Eye, History, RefreshCw, Undo2 } from "lucide-react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { EmptyState } from "../../ui/EmptyState";
import { Skeleton } from "../../ui/skeleton";
import { StatusPill } from "../../ui/StatusPill";
import { useBuilder } from "../builder-context";
import { usePageRevisions } from "../../../hooks/site-builder/usePageRevisions";
import { revisionAuthor, revisionNote } from "../../../lib/site-builder/page-status";
import { formatShopDateTime } from "../../../lib/shop-time";

/** Сколько версий за раз. */
const PAGE_SIZE = 20;

interface Props {
  open: boolean;
  onClose: () => void;
  onRollback: (version: number) => void;
}

export function PageVersionsDialog({ open, onClose, onRollback }: Props) {
  const b = useBuilder();
  const changes = b.publishing.changes;
  const revisions = usePageRevisions(open ? (changes?.pageId ?? null) : null, PAGE_SIZE, changes?.live?.version ?? null);
  const canRollback = b.canPublishPages && b.canEditPages;
  // «на сайте» — только у видимой страницы (QA Р3а D12).
  const shown = changes?.isPublished ?? true;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen="lg" className="flex flex-col lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="line-clamp-2 break-words">Все версии · {changes?.title ?? ""}</DialogTitle>
          <DialogDescription>Каждая публикация — версия. Версии храним не меньше 12 месяцев и всегда не меньше 10 последних.</DialogDescription>
        </DialogHeader>
        {revisions.error ? (
          <div className="flex flex-col items-start gap-2">
            <p className="type-body text-on-surface-variant">{revisions.error}</p>
            <Button variant="outline" onClick={revisions.reload}>
              <RefreshCw aria-hidden />
              Повторить
            </Button>
          </div>
        ) : null}
        {revisions.loading && revisions.items.length === 0 ? <Skeleton className="h-40 rounded-xl" aria-label="Загружаем версии" /> : null}
        {!revisions.loading && !revisions.error && revisions.items.length === 0 ? (
          <EmptyState icon={History} title="Версий пока нет" description="Страница ещё не была на сайте." />
        ) : null}
        <ul className="flex min-h-0 flex-col gap-2 overflow-y-auto">
          {revisions.items.map((r) => (
            <li key={r.version} className="flex flex-col gap-2 rounded-xl bg-surface-low p-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="type-body flex flex-wrap items-center gap-2 font-medium text-on-surface">
                  Версия {r.version}
                  {r.isLive ? <StatusPill tone={shown ? "success" : "neutral"}>{shown ? "на сайте" : "текущая · страница скрыта"}</StatusPill> : null}
                </span>
                <span className="type-caption text-on-surface-variant">
                  {formatShopDateTime(r.publishedAt)} · {revisionAuthor(r)}
                </span>
                <span className="type-caption break-words text-on-surface">{revisionNote(r)}</span>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => b.viewPageVersion(r)}>
                  <Eye aria-hidden />
                  Смотреть
                </Button>
                {!r.isLive && canRollback ? (
                  <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => onRollback(r.version)}>
                    <Undo2 aria-hidden />
                    Вернуть
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        {revisions.hasMore ? (
          <Button variant="outline" className="self-center" loading={revisions.loading} onClick={revisions.loadMore}>
            Показать ещё
          </Button>
        ) : null}
        {canRollback ? null : <p className="type-caption text-on-surface-variant">Вернуть версию может сотрудник с правом публикации и правом контента.</p>}
      </DialogContent>
    </Dialog>
  );
}
